import React from 'react';
import { Phone, Mail, MessageCircle, Linkedin, DollarSign } from 'lucide-react';
import type { PipelineStageConfig } from '../../hooks/usePipelineStagesConfig';

interface PipelineTableProps {
  leads: any[];
  onViewDetails: (lead: any) => void;
  stages: PipelineStageConfig[];
}

const STAGE_COLORS = [
  'bg-amber-50 text-amber-700 border-amber-200/50',
  'bg-[#1875FF]/10 text-[#1875FF] border-[#1875FF]/20',
  'bg-cyan-50 text-cyan-700 border-cyan-200/50',
  'bg-teal-50 text-teal-700 border-teal-200/50',
  'bg-sky-50 text-sky-700 border-sky-200/50',
  'bg-emerald-50 text-emerald-700 border-emerald-200/50',
  'bg-green-50 text-green-700 border-green-200/50',
  'bg-rose-50 text-rose-700 border-rose-200/50',
  'bg-gray-100 text-gray-600 border-gray-200',
  'bg-orange-50 text-orange-700 border-orange-200/50',
  'bg-slate-100 text-slate-600 border-slate-200',
  'bg-zinc-100 text-zinc-600 border-zinc-200',
];

function formatDealValue(val: number | null | undefined): string {
  if (val == null || val <= 0) return '-';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

const PipelineTable: React.FC<PipelineTableProps> = ({ leads, onViewDetails, stages }) => {
  const getChannelIcon = (channel: string) => {
    if (!channel || channel.trim() === '') {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border-2 border-gray-200"><MessageCircle size={12} className="mr-1" />Unknown</span>;
    }

    const normalized = channel.toString().toLowerCase().trim();
    switch (normalized) {
      case 'email':
      case 'email agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border-2 border-blue-200/50"><Mail size={12} className="mr-1" />Email</span>;
      case 'call':
      case 'call agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#1875FF]/10 text-[#1875FF] border-2 border-[#1875FF]/30"><Phone size={12} className="mr-1" />Call</span>;
      case 'whatsapp':
      case 'whatsapp agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#52B5AE]/10 text-[#52B5AE] border-2 border-[#52B5AE]/30"><MessageCircle size={12} className="mr-1" />WhatsApp</span>;
      case 'linkedin':
      case 'linkedin agent':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#05E0D1]/10 text-[#05E0D1] border-2 border-[#05E0D1]/30"><Linkedin size={12} className="mr-1" />LinkedIn</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border-2 border-gray-200"><MessageCircle size={12} className="mr-1" />{channel}</span>;
    }
  };

  const stageColorMap: Record<string, string> = {};
  stages.forEach((s, i) => {
    stageColorMap[s.id] = STAGE_COLORS[i % STAGE_COLORS.length];
    stageColorMap[s.name.toLowerCase().replace(/\s+/g, '_')] = STAGE_COLORS[i % STAGE_COLORS.length];
    stageColorMap[s.name.toLowerCase()] = STAGE_COLORS[i % STAGE_COLORS.length];
  });

  const getStageColor = (stage: string) => {
    const normalized = (stage || '').toLowerCase().replace(/\s+/g, '_');
    return stageColorMap[normalized] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusColor = (status: string) => {
    const normalized = (status || '').toLowerCase().trim();
    switch (normalized) {
      case 'chatting':
      case 'talking':
        return 'bg-[#52B5AE]/10 text-[#52B5AE] border-2 border-[#52B5AE]/30';
      case 'follow up':
        return 'bg-[#1875FF]/10 text-[#1875FF] border-2 border-[#1875FF]/30';
      case 'booked':
        return 'bg-[#05E0D1]/10 text-[#05E0D1] border-2 border-[#05E0D1]/30';
      default:
        return 'bg-gray-100 text-gray-700 border-2 border-gray-200';
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
            <tr>
              <th className="text-left px-6 py-4 text-[11px] font-semibold text-gray-600 uppercase tracking-widest font-sans">Company</th>
              <th className="text-left px-6 py-4 text-[11px] font-semibold text-gray-600 uppercase tracking-widest font-sans">Contact</th>
              <th className="text-center px-6 py-4 text-[11px] font-semibold text-gray-600 uppercase tracking-widest font-sans">Stage</th>
              <th className="text-center px-6 py-4 text-[11px] font-semibold text-gray-600 uppercase tracking-widest font-sans">Deal Value</th>
              <th className="text-center px-6 py-4 text-[11px] font-semibold text-gray-600 uppercase tracking-widest font-sans">Channel</th>
              <th className="text-center px-6 py-4 text-[11px] font-semibold text-gray-600 uppercase tracking-widest font-sans">Status</th>
              <th className="text-center px-6 py-4 text-[11px] font-semibold text-gray-600 uppercase tracking-widest font-sans">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1875FF]/10">
            {leads.map((lead) => (
              <tr key={lead.deal_id} className="hover:bg-[#1875FF]/5 transition-all group">
                <td className="px-6 py-4">
                  <div>
                    <div className="font-bold text-gray-900">{lead.company_name || 'Unknown Company'}</div>
                    <div className="text-sm text-gray-600 mt-0.5 font-medium">{lead.industry_1 || 'Unknown Industry'}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-semibold text-gray-800">{lead.dm_full_name || 'Unknown'}</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border-2 ${getStageColor(lead.pipeline_stage)}`}>
                    {lead.pipeline_stage || 'Unknown'}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className="text-sm font-semibold text-gray-800">
                    {lead.deal_value ? (
                      <span className="flex items-center justify-center gap-0.5 text-emerald-600 font-metric">
                        <DollarSign size={12} />
                        {formatDealValue(lead.deal_value).replace('$', '')}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    {getChannelIcon(lead.channel_booked)}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(lead.ovrall_lead_status)}`}>
                    {lead.ovrall_lead_status || 'Open'}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => onViewDetails(lead)}
                    className="text-[#1875FF] hover:text-[#05E0D1] font-semibold text-sm transition-colors"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {leads.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-700 text-lg font-bold font-display">No deals found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">
            Try adjusting your filters or search terms
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineTable;
